import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Logo, Check } from '../components/Icons.jsx';
import { Intro, Done, mainStyle } from './VennArchitect.jsx';
import DrillResultBar from '../components/DrillResultBar.jsx';

const levels = {
  easy: { range: [10, 99], ops: ['+', '−'], label: 'Easy', sub: 'Two-digit · +, −' },
  medium: { range: [10, 999], ops: ['+', '−', '×'], label: 'Medium', sub: 'Mixed · +, −, ×' },
  hard: { range: [100, 9999], ops: ['+', '−', '×', '÷'], label: 'Hard', sub: 'Large numbers · all 4 ops' }
};

function makeQuestion(level) {
  const L = levels[level];
  const op = L.ops[Math.floor(Math.random() * L.ops.length)];
  const [lo, hi] = L.range;
  const r = () => lo + Math.floor(Math.random() * (hi - lo + 1));
  let a, b, answer;
  if (op === '+') { a = r(); b = r(); answer = a + b; }
  else if (op === '−') { a = r(); b = r(); if (b > a) [a, b] = [b, a]; answer = a - b; }
  else if (op === '×') { a = 2 + Math.floor(Math.random() * 30); b = 2 + Math.floor(Math.random() * 30); answer = a * b; }
  else {
    b = 2 + Math.floor(Math.random() * 12);
    const factor = 2 + Math.floor(Math.random() * 50);
    a = b * factor;
    answer = factor;
  }
  return { a, b, op, answer };
}

const keys = ['7','8','9','4','5','6','1','2','3','0','.','⌫'];

export default function NumpadTrainer() {
  const [stage, setStage] = useState('intro');
  const [level, setLevel] = useState('medium');
  const [ucatsen, setUcatsen] = useState(false);
  const [q, setQ] = useState(null);
  const [input, setInput] = useState('');
  const [score, setScore] = useState({ correct: 0, wrong: 0, attempts: 0 });
  const [secondsLeft, setSecondsLeft] = useState(90);
  const [flash, setFlash] = useState(null);
  const [history, setHistory] = useState('');
  const timerRef = useRef(null);

  const duration = ucatsen ? 113 : 90;

  useEffect(() => {
    if (stage !== 'drill') return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => { if (s <= 1) { clearInterval(timerRef.current); setStage('done'); return 0; } return s - 1; });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [stage]);

  useEffect(() => {
    if (stage !== 'drill') return;
    const onKey = (e) => {
      if (/^[0-9.]$/.test(e.key)) {
        setInput((i) => i + e.key);
        setHistory(`Typed: ${e.key}`);
      } else if (e.key === 'Backspace') {
        setInput((i) => i.slice(0, -1));
        setHistory('⌫');
      } else if (e.key === 'Enter') {
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage, input, q]);

  const start = () => {
    setScore({ correct: 0, wrong: 0, attempts: 0 });
    setSecondsLeft(duration); setInput(''); setFlash(null);
    setQ(makeQuestion(level)); setStage('drill');
  };

  const press = (k) => {
    if (k === '⌫') { setInput((i) => i.slice(0, -1)); return; }
    setInput((i) => i + k);
  };

  const submit = () => {
    if (!q || input.trim() === '') return;
    const guess = Number(input);
    const ok = guess === q.answer;
    setScore((s) => ({ correct: s.correct + (ok ? 1 : 0), wrong: s.wrong + (ok ? 0 : 1), attempts: s.attempts + 1 }));
    setFlash({ ok, msg: ok ? '✓' : `${q.answer}` });
    setInput(''); setQ(makeQuestion(level));
    setTimeout(() => setFlash(null), 600);
  };

  const accuracy = score.attempts > 0 ? Math.round((score.correct / score.attempts) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={drillHeader}>
        <Link to="/practice" style={drillExit}><ArrowLeft width="16" height="16" />Exit</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight /><strong style={titleStyle}>Numpad trainer</strong>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: secondsLeft < 15 ? '#b91c1c' : 'var(--text-2)', minWidth: 60, textAlign: 'right' }}>
          {stage === 'drill' && `${secondsLeft}s`}
        </div>
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="QR" title="Numpad trainer"
          body="Practice typing on the on-screen calc with your physical numpad. Real UCAT lets you use either — the people who train both win."
          how={['Use your keyboard numpad to enter answers', 'Enter submits, Backspace deletes', 'Click the on-screen pad if you prefer touch']}
          difficulty={level} setDifficulty={setLevel}
          difficultyOptions={Object.entries(levels).map(([v, L]) => ({ v, label: L.label, sub: L.sub }))}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
        />
      )}

      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>
            <span style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{levels[level].label}</span>
            <span><strong style={{ color: 'var(--live-green)' }}>{score.correct}</strong> · <strong style={{ color: '#b91c1c' }}>{score.wrong}</strong></span>
          </div>

          <div style={{
            background: flash?.ok === true ? '#e7f7ef' : flash?.ok === false ? '#fee2e2' : '#fff',
            border: `2px solid ${flash?.ok === true ? 'var(--live-green)' : flash?.ok === false ? '#b91c1c' : 'var(--border)'}`,
            borderRadius: 16, padding: '32px 20px', textAlign: 'center', marginBottom: 14
          }}>
            <div style={{ fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', color: 'var(--brand-deep)' }}>
              {q.a} {q.op} {q.b} =
            </div>
            <div style={{ marginTop: 10, fontSize: 'clamp(28px, 6vw, 36px)', fontWeight: 700, color: input ? 'var(--brand-purple)' : 'var(--text-muted)', minHeight: 44, fontFamily: 'monospace' }}>
              {input || '·'}
            </div>
            {flash && <div style={{ fontSize: 22, fontWeight: 800, color: flash.ok ? 'var(--live-green)' : '#b91c1c' }}>{flash.msg}</div>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxWidth: 320, margin: '0 auto' }}>
            {keys.map((k) => (
              <button key={k} onClick={() => press(k)} style={{
                padding: '20px 0', fontSize: 22, fontWeight: 700, cursor: 'pointer',
                border: '1px solid var(--border-strong)', background: '#fff',
                borderRadius: 12, fontFamily: 'Montserrat, sans-serif',
                color: k === '⌫' ? '#b91c1c' : 'var(--brand-deep)'
              }}>{k}</button>
            ))}
          </div>
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button onClick={submit} className="btn btn-primary" style={{ minWidth: 200 }}>Submit (Enter) <Check width="14" height="14" /></button>
          </div>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="QR" title="Numpad trainer" stats={[
          { label: 'Correct', value: score.correct },
          { label: 'Per minute', value: Math.round(score.correct / (duration / 60)) },
          { label: 'Accuracy', value: `${accuracy}%` }
        ]} onRestart={start}
          extra={<DrillResultBar slug="numpad-trainer" score={score.correct} accuracyPct={accuracy} metadata={{ level, ucatsen }} />}
        />
      )}
    </div>
  );
}

const drillHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10 };
const drillExit = { display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 };
const titleStyle = { fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' };
